import { Formik } from 'formik'
import React, { useState } from 'react'
import { EditFieldFormValues } from './EditFieldForm.interface'
import { getEditFieldSchema } from './EditFieldForm.validator'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { handleApiError } from 'src/utils/errorHandlers'
import { FormikSelect } from '../form/formik/FormikSelect'
import { BasicSelectOption } from '../form/BasicSelect'
import { FormikDatePicker } from '../form/formik/FormikDatePicker'
import { Dayjs } from 'dayjs'
import { WarningContainer } from 'src/components/shared/WarningContainer'

interface Props {
  label: string
  initialValue?: string | null | number
  placeholder?: string
  options?: Array<BasicSelectOption>
  editFiledType?: 'input' | 'select' | 'date' | 'email'
  minDateError?: string
  minDate?: Dayjs
  maxDate?: Dayjs
  requiredError?: string
  /** Title of an informational banner shown under the field. Banner renders only when set. */
  warningTitle?: string
  warningSubtitle?: string
  onSubmit(value: string): void | Promise<void>
  onClose(): void
  /** Stable prefix for `data-testid` on this form and fields (set per screen when reused). */
  dataTestIdPrefix?: string
}

export const EditFieldForm = ({
  label,
  placeholder,
  initialValue,
  editFiledType = 'input',
  options = [],
  minDate,
  minDateError,
  maxDate,
  requiredError,
  warningTitle,
  warningSubtitle,
  onSubmit,
  onClose,
  dataTestIdPrefix,
}: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleFormSubmit = async (values: EditFieldFormValues) => {
    setIsLoading(true)

    try {
      await onSubmit(values.value)
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      initialValues={{ value: initialValue?.toString() || '' }}
      onSubmit={handleFormSubmit}
      validationSchema={getEditFieldSchema(label, editFiledType, requiredError)}
    >
      {({ handleSubmit }) => (
        <div data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-form` : undefined}>
          <div css={tw`px-4 pt-4`}>
            {warningTitle && (
              <div css={tw`w-[304px] mb-4`}>
                <WarningContainer title={warningTitle} subtitle={warningSubtitle} />
              </div>
            )}
            {(editFiledType === 'input' || editFiledType === 'email') && (
              <FormikTextField
                name="value"
                css={tw`w-[304px]`}
                label={label}
                placeholder={placeholder}
                multiline
                inputProps={
                  dataTestIdPrefix
                    ? { 'data-testid': `${dataTestIdPrefix}-value-input` }
                    : undefined
                }
              />
            )}
            {editFiledType === 'select' && (
              <div css={tw`w-[304px]`}>
                <FormikSelect
                  name="value"
                  label={label}
                  options={options}
                  inputProps={
                    dataTestIdPrefix
                      ? { 'data-testid': `${dataTestIdPrefix}-value-select` }
                      : undefined
                  }
                />
              </div>
            )}
            {editFiledType === 'date' && (
              <div css={tw`w-[304px]`}>
                <FormikDatePicker
                  name="value"
                  label={label}
                  minDate={minDate}
                  minDateError={minDateError}
                  maxDate={maxDate}
                  slotProps={
                    dataTestIdPrefix
                      ? {
                          textField: {
                            inputProps: { 'data-testid': `${dataTestIdPrefix}-value-input` },
                          },
                        }
                      : undefined
                  }
                />
              </div>
            )}
          </div>
          <div
            css={tw`flex flex-row justify-between items-end  border-0 border-t border-solid border-[#EDEDED] mt-4 p-4 pt-3`}
          >
            <Button
              variant="contained"
              disabled={isLoading}
              size="small"
              color="grey"
              onClick={onClose}
              data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-cancel-btn` : undefined}
            >
              Cancel
            </Button>
            <div css={tw`flex flex-col items-end`}>
              <Button
                variant="contained"
                size="small"
                loading={isLoading}
                onClick={() => handleSubmit()}
                data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-save-btn` : undefined}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </Formik>
  )
}
